// The Amis book prints a 1970s Catholic liturgical orthography: ^ where the
// modern spelling has a letter, nothing, or different vowels entirely. A reader
// who learned the standard spelling does not recognise pala^ as pala. This
// overlay lets them read the modern form — and it changes what is PRINTED and
// nothing else.
//
// Display-only is the whole safety argument, in two parts:
//
//  1. Words are per-dialect identities keyed "<dialect>:<word>"
//     (utils/wordText.js). A swap that reached the key would silently fork one
//     word's vocab, SRS and gloss-correction state into two piles that never
//     merge, on a device that has already synced the first pile.
//  2. It re-opens the tolo^ trap. tolo^ is 三; tolo' is 跌倒. The lookup must
//     keep seeing exactly what the book wrote, or a correct gloss turns into a
//     confidently wrong one. Only the glyph on screen is safe to touch.
//
// A table, never a rule. ^ does at least three different jobs in this one book
// (nothing in pala^, an e in ka^mangay, different vowels again in cika^), so
// mechanical folding would be wrong more often than right. Every pair is
// hand-verified against the dictionary or a native speaker, and a word with no
// verified pair prints unchanged — 寧可從缺 applies to spelling too.
//
// The table rides the dictionaries' R2 path, stamp and must-revalidate policy
// (hence the dict_ prefix, as with dict_thao_acd.json), so adding a pair costs
// a push and not a deploy.
import { DICT_BASE, DICT_V } from './api';

// Written out rather than derived, for the reason FILES in dict.js is: a derived
// name that is subtly wrong does not throw, it 404s, and a 404 here is
// indistinguishable from "this dialect has no pairs".
const FILES = { amis: 'dict_amis_orth.json' };

// Drives whether the reader shows the toggle at all. Data-driven rather than
// `dialect === 'amis'` so the Kavalan glides and Puyuma's kemiyager can be
// switched on by shipping a file, without touching this code again.
export const hasOrth = (dialect) => !!FILES[dialect];

// dialect -> Promise<table>. The promise is cached, not the result, so a
// remount mid-flight shares the one request.
const loaded = new Map();

export function loadOrth(dialect) {
  const file = FILES[dialect];
  if (!file) return Promise.resolve(null);
  let p = loaded.get(dialect);
  if (!p) {
    p = fetch(`${DICT_BASE}/${file}${DICT_V}`)
      .then((r) => { if (!r.ok) throw new Error(`${file}: HTTP ${r.status}`); return r.json(); });
    // Same rule as dict.js: drop the rejected promise so the next mount retries.
    // Cached, it would replay one dropped connection for the whole session.
    p.catch(() => loaded.delete(dialect));
    loaded.set(dialect, p);
  }
  return p;
}

// Fold apostrophes the way dict.js's norm() does: ’ and ʼ and ' are one letter,
// and which one a file carries is an accident of where its text was typed.
const key = (w) => (w || '').toLowerCase().replace(/[ʼ’‘]/g, "'");

// Keep the book's capitalisation — the table is lowercase but the word may open
// a sentence (Mipatala^). Case rides on the first LETTER, not the first
// character: ’Afofo opens with a glottal, and uppercasing that does nothing.
const recase = (from, to) => {
  const i = from.search(/[a-z]/i);
  const j = to.search(/[a-z]/i);
  if (i < 0 || j < 0 || from[i] !== from[i].toUpperCase()) return to;
  return to.slice(0, j) + to[j].toUpperCase() + to.slice(j + 1);
};

// The renderers' entry point. Identity when the table is absent (not loaded,
// fetch failed, dialect has none) or silent about this word, so every failure
// mode degrades to the book's own spelling rather than to a blank.
export function orthForm(table, word) {
  if (!table || !word) return word;
  const to = table[key(word)];
  return to ? recase(word, to) : word;
}
